import { acceptHMRUpdate, defineStore } from 'pinia'

import api from '@/lib/api'

import {
  transformOrderTemplate,
  transformApiFormErrors,
} from '@/components/cart/helpers/transformHelpers'

import { useSessionStore } from './session'

const defaultOrderTemplateData = {
  id: '',
  active: true,
  b2bUnit: '',
  b2bUnitName: '',
  department: {},
  billTo: '',
  billToAddress: {},
  billToAddressString: '',
  billingContact: '',
  billingEmails: [],
  contact: '',
  contactName: '',
  contactEmail: '',
  name: '',
  expirationDate: '',
  notes: '',
  poNumber: '',
  shipTo: '',
  shipToAddress: {},
  shipToAddressString: '',
  shippingContact: '',
  shippingEmails: [],
}

export const useOrderTemplateStore = defineStore('order-template', {
  state: () => ({
    modes: {
      new: 'NEW',
      edit: 'EDIT',
      copy: 'COPY',
    },
    activeMode: null,
    orderTemplateFormData: { ...defaultOrderTemplateData },
  }),

  actions: {
    async getCompanyOrderTemplates({ company = '', department = '' }) {
      const response = await api.inventoryTracking.getOrderTemplates({
        pageSize: 9999,
        company,
        department,
      })
      const { orderTemplates } = response.data
      return orderTemplates.map(transformOrderTemplate)
    },

    setupOrderTemplateForm({ mode, orderTemplateData }) {
      const sessionStore = useSessionStore()
      const userCompany = {
        code: sessionStore.user?.unit?.uid,
        name: sessionStore.user?.unit?.name,
      }
      const formData =
        mode === this.modes.new
          ? {
              ...defaultOrderTemplateData,
              b2bUnit: userCompany,
              billTo: userCompany,
              shipTo: userCompany,
            }
          : { ...orderTemplateData }
      this.orderTemplateFormData = formData
      this.activeMode = mode
    },

    async sendOrderTemplate(orderTemplateData) {
      const handlers = {
        [this.modes.new]: api.inventoryTracking.addNewOrderTemplate,
        [this.modes.copy]: api.inventoryTracking.addNewOrderTemplate,
        [this.modes.edit]: api.inventoryTracking.updateOrderTemplate,
      }

      try {
        return handlers[this.activeMode](orderTemplateData)
      } catch (error) {
        const transformedError = new Error('Threshold validation error')
        transformedError.response = {
          data: transformApiFormErrors(error.response.data),
        }
        throw transformedError
      }
    },

    async validateOrderTemplate(thresholdData) {
      try {
        await api.inventoryTracking.validateOrderTemplate(thresholdData)
        return { ok: true }
      } catch (error) {
        return {
          ok: false,
          errors: transformApiFormErrors(error.response.data),
        }
      }
    },

    resetState() {
      this.$reset()
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useOrderTemplateStore, import.meta.hot))
}
